"use client";
import { Sports } from "@web/lib/data";
import dynamic from "next/dynamic";
import { startTransition, useEffect, useState } from "react";

type BrowserWindow = Window &
  typeof globalThis & {
    requestIdleCallback?: (
      callback: IdleRequestCallback,
      options?: IdleRequestOptions,
    ) => number;
    cancelIdleCallback?: (handle: number) => void;
  };

const Planet = dynamic(
  async () => (await import("@web/components/Planet")).default,
  {
    ssr: false,
  },
);

export default function PlanetServer({
  sport,
  className,
}: {
  sport: Sports;
  className?: string;
}) {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const browserWindow = window as BrowserWindow;
    const mediaQuery = browserWindow.matchMedia("(min-width: 1280px)");
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let idleCallbackId: number | undefined;

    const scheduleEnable = () => {
      if (!mediaQuery.matches) {
        setEnabled(false);
        return;
      }

      if (browserWindow.requestIdleCallback) {
        idleCallbackId = browserWindow.requestIdleCallback(
          () => {
            startTransition(() => {
              setEnabled(true);
            });
          },
          { timeout: 1500 },
        );
        return;
      }

      timeoutId = setTimeout(() => {
        startTransition(() => {
          setEnabled(true);
        });
      }, 250);
    };

    const handleMediaChange = (event: MediaQueryListEvent) => {
      if (!event.matches) {
        setEnabled(false);
        return;
      }

      scheduleEnable();
    };

    scheduleEnable();
    mediaQuery.addEventListener("change", handleMediaChange);

    return () => {
      mediaQuery.removeEventListener("change", handleMediaChange);

      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }

      if (idleCallbackId !== undefined && browserWindow.cancelIdleCallback) {
        browserWindow.cancelIdleCallback(idleCallbackId);
      }
    };
  }, []);

  if (!enabled) {
    return <div aria-hidden className={className} />;
  }

  return <Planet sport={sport} className={className} />;
}
